// src/pages/ManageAdmins.tsx - Fixed query issues
import React, {useState} from 'react';
import {useQuery, useMutation, useQueryClient} from '@tanstack/react-query';
import {supabase} from '@/services/supabase';
import {Badge, Button, LoadingSpinner, Modal, Table} from '@/components/ui';
import {Plus, Crown, Mail, Trash2, Send} from 'lucide-react';
import toast from 'react-hot-toast';
import {useAuth} from '@/context/AuthContext';
import {isSuperAdmin} from '@/utils/auth';
import {AdminUser, CreateAdminUserData} from '@/types';

const ManageAdmins: React.FC = () => {
    const {user} = useAuth();
    const queryClient = useQueryClient();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [sortBy, setSortBy] = useState<string>('created_at');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
    const [formData, setFormData] = useState<CreateAdminUserData>({
        email: '',
        first_name: '',
        last_name: '',
        full_name: '',
        role: 'super_admin',
        organization_id: undefined,
    });

    // Check if user is super admin
    if (!isSuperAdmin(user)) {
        return (
            <div className="p-6">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-red-700">Access denied. Super admin privileges required.</p>
                </div>
            </div>
        );
    }

    // Fetch super admin users only
    const {data: admins = [], isLoading} = useQuery({
        queryKey: ['super-admin-users'],
        queryFn: async (): Promise<AdminUser[]> => {
            console.log('🔍 Fetching super admin users...');

            const {data, error} = await supabase
                .from('admin_users')
                .select('*')
                .eq('role', 'super_admin') // Only super admins
                .order('created_at', {ascending: false});

            if (error) {
                console.error('❌ Super admin users query error:', error);
                throw error;
            }

            console.log('✅ Super admin users fetched:', data?.length || 0);
            return data || [];
        },
    });

    // Create super admin mutation
    const createAdminMutation = useMutation({
        mutationFn: async (data: any) => {
            try {
                console.log('➕ Creating admin user via Edge Function:', data.email);

                const {data: {session}} = await supabase.auth.getSession();
                if (!session?.access_token) {
                    throw new Error('No authentication token available');
                }

                const invitationUrl = `${window.location.origin}/accept-admin-invitation`;

                // Call our Edge Function that handles everything
                const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-admin-user`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${session.access_token}`,
                    },
                    body: JSON.stringify({
                        email: data.email,
                        first_name: data.first_name,
                        last_name: data.last_name,
                        full_name: data.full_name,
                        role: data.role,
                        organization_id: data.organization_id || null,
                        redirectTo: invitationUrl
                    })
                });

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(errorData.error || `Failed to create admin user: ${response.statusText}`);
                }

                const result = await response.json();
                console.log('✅ Admin user created:', result);

                return result.data;

            } catch (error) {
                console.error('Create admin error:', error);
                throw error;
            }
        },
        onSuccess: (data) => {
            toast.success(`Super admin created and invitation sent to ${data.email}!`);
            queryClient.invalidateQueries({queryKey: ['super-admin-users']});
            setIsModalOpen(false);
            resetForm();
        },
        onError: (error: any) => {
            toast.error(error.message || 'Failed to create admin user');
        },
    });

    // Delete super admin mutation
    const deleteAdminMutation = useMutation({
        mutationFn: async (adminId: string) => {
            // Step 1: Get admin details first
            const {data: admin, error: fetchError} = await supabase
                .from('admin_users')
                .select('email')
                .eq('id', adminId)
                .single();

            if (fetchError || !admin) {
                throw new Error('Admin user not found');
            }

            // Step 2: Delete the admin_users record first
            const {error: dbDeleteError} = await supabase
                .from('admin_users')
                .delete()
                .eq('id', adminId);

            if (dbDeleteError) {
                throw new Error(`Failed to delete admin record: ${dbDeleteError.message}`);
            }

            // Step 3: Try to delete the auth user using the edge function (if exists)
            try {
                const {data: {session}} = await supabase.auth.getSession();
                if (session?.access_token) {
                    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/delete-auth-user`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${session.access_token}`,
                        },
                        body: JSON.stringify({
                            email: admin.email
                        })
                    });

                    // Don't throw if this fails - the main deletion (admin_users) was successful
                    if (!response.ok) {
                        console.warn(`Auth user deletion failed for ${admin.email}, but admin record was deleted`);
                    } else {
                        console.log(`✅ Auth user deleted for ${admin.email}`);
                    }
                }
            } catch (authDeleteError) {
                console.warn('Auth user cleanup failed:', authDeleteError);
                // Don't throw - the main deletion was successful
            }

            return adminId;
        },
        onSuccess: () => {
            toast.success('Admin user deleted successfully!');
            queryClient.invalidateQueries({queryKey: ['super-admin-users']});
        },
        onError: (error: any) => {
            toast.error(error.message || 'Failed to delete admin user');
        },
    });

    // Resend invitation mutation
    const resendInvitationMutation = useMutation({
        mutationFn: async (admin: AdminUser) => {
            try {
                const {data: {session}} = await supabase.auth.getSession();
                if (!session?.access_token) {
                    throw new Error('No authentication token available');
                }

                const invitationUrl = `${window.location.origin}/accept-admin-invitation`;

                const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-admin-user`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${session.access_token}`,
                    },
                    body: JSON.stringify({
                        email: admin.email,
                        first_name: admin.first_name,
                        last_name: admin.last_name,
                        full_name: admin.full_name,
                        role: admin.role,
                        organization_id: admin.organization_id || null,
                        redirectTo: invitationUrl
                    })
                });

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(errorData.error || `Failed to resend invitation: ${response.statusText}`);
                }

                const result = await response.json();
                return result.data;

            } catch (error) {
                console.error('Resend invitation error:', error);
                throw error;
            }
        },
        onSuccess: (_data, admin) => {
            toast.success(`Invitation resent to ${admin.email}!`);
            queryClient.invalidateQueries({queryKey: ['super-admin-users']});
        },
        onError: (error: any, admin) => {
            toast.error(error.message || `Failed to resend invitation to ${admin.email}`);
        },
    });

    const handleSort = (column: string, order: 'asc' | 'desc'): void => {
        setSortBy(column);
        setSortOrder(order);
    };

    // Client-side sorting
    const sortedAdmins = React.useMemo(() => {
        if (!admins || admins.length === 0) return [];

        return [...admins].sort((a, b) => {
            let aValue: any = a[sortBy as keyof AdminUser] ?? '';
            let bValue: any = b[sortBy as keyof AdminUser] ?? '';

            if (typeof aValue === 'string' && typeof bValue === 'string') {
                const comparison = aValue.toLowerCase().localeCompare(bValue.toLowerCase());
                return sortOrder === 'asc' ? comparison : -comparison;
            }

            if (sortBy.includes('_at') || sortBy.includes('date')) {
                const aDate = new Date(aValue).getTime() || 0;
                const bDate = new Date(bValue).getTime() || 0;
                return sortOrder === 'asc' ? aDate - bDate : bDate - aDate;
            }

            return 0;
        });
    }, [admins, sortBy, sortOrder]);

    const handleInputChange = (field: keyof CreateAdminUserData, value: string) => {
        setFormData((prev: CreateAdminUserData) => ({
            ...prev,
            [field]: value,
            // Auto-generate full_name when first_name or last_name changes
            ...(field === 'first_name' || field === 'last_name' ? {
                full_name: `${field === 'first_name' ? value : prev.first_name} ${field === 'last_name' ? value : prev.last_name}`.trim()
            } : {})
        }));
    };

    const resetForm = () => {
        setFormData({
            email: '',
            first_name: '',
            last_name: '',
            full_name: '',
            role: 'super_admin', // Always super_admin
            organization_id: undefined,
        });
    };

    const handleCreateAdmin = async (e: React.FormEvent) => {
        e.preventDefault();

        // Validation - simplified for super admin only
        if (!formData.email || !formData.first_name || !formData.last_name) {
            toast.error('Please fill in all required fields');
            return;
        }

        createAdminMutation.mutate(formData);
    };

    const handleDeleteAdmin = (admin: AdminUser) => {
        if (admin.id === user?.id) {
            toast.error('You cannot delete your own account');
            return;
        }

        if (window.confirm(`Are you sure you want to delete ${admin.full_name}?`)) {
            deleteAdminMutation.mutate(admin.id);
        }
    };

    const handleResendInvitation = (admin: AdminUser) => {
        if (window.confirm(`Resend invitation email to ${admin.full_name} (${admin.email})?`)) {
            resendInvitationMutation.mutate(admin);
        }
    };

    // Table columns configuration
    const columns = [
        {
            key: 'full_name',
            title: 'User',
            sortable: true,
            render: (value: string, record: AdminUser) => (
                <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-yellow-100 flex items-center justify-center">
                            <Crown className="w-5 h-5 text-yellow-600"/>
                        </div>
                    </div>
                    <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{value}</div>
                        <div className="text-sm text-gray-500 flex items-center gap-1">
                            <Mail className="w-3 h-3"/>
                            {record.email}
                        </div>
                    </div>
                </div>
            ),
        },
        {
            key: 'status',
            title: 'Status',
            sortable: true,
            render: (value: string) => (
                <Badge variant={value === 'active' ? 'success' : 'default'}>
                    {value === 'active' ? 'Active' : 'Inactive'}
                </Badge>
            ),
        },
        {
            key: 'created_at',
            title: 'Created',
            sortable: true,
            render: (value: string) => new Date(value).toLocaleDateString(),
        },
        {
            key: 'actions',
            title: 'Actions',
            align: 'right' as const,
            render: (_: any, record: AdminUser) => (
                <div className="flex items-center justify-end gap-2">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleResendInvitation(record)}
                        title="Resend Invitation"
                    >
                        <Send className="w-4 h-4"/>
                    </Button>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteAdmin(record)}
                        disabled={record.id === user?.id}
                        title="Delete Admin"
                    >
                        <Trash2 className="w-4 h-4"/>
                    </Button>
                </div>
            ),
        },
    ];

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <LoadingSpinner size="lg"/>
            </div>
        );
    }

    return (
        <div className="p-6 space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Manage Super Admins</h1>
                    <p className="mt-1 text-sm text-gray-500">
                        Create and manage super administrator users with full system access
                    </p>
                </div>
                <Button
                    onClick={() => setIsModalOpen(true)}
                    className="flex items-center gap-2"
                >
                    <Plus className="w-4 h-4"/>
                    Add Super Admin
                </Button>
            </div>

            {/* Admin Users Table */}
            <div className="bg-white shadow rounded-lg overflow-hidden">
                {admins.length === 0 ? (
                    <div className="p-8 text-center">
                        <Crown className="w-12 h-12 text-yellow-400 mx-auto mb-4"/>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No super admin users found</h3>
                        <p className="text-gray-500 mb-4">
                            Create your first super administrator to manage the platform.
                        </p>
                        <Button onClick={() => setIsModalOpen(true)}>
                            Add Super Admin
                        </Button>
                    </div>
                ) : (
                    <Table
                        columns={columns}
                        data={sortedAdmins}
                        loading={isLoading}
                        sortBy={sortBy}
                        sortOrder={sortOrder}
                        onSort={handleSort}
                    />
                )}
            </div>

            {/* Create Super Admin Modal */}
            <Modal
                isOpen={isModalOpen}
                onClose={() => {
                    setIsModalOpen(false);
                    resetForm();
                }}
                title="Create Super Admin User"
            >
                <form onSubmit={handleCreateAdmin} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                First Name *
                            </label>
                            <input
                                type="text"
                                required
                                value={formData.first_name}
                                onChange={(e) => handleInputChange('first_name', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Last Name *
                            </label>
                            <input
                                type="text"
                                required
                                value={formData.last_name}
                                onChange={(e) => handleInputChange('last_name', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Email *
                        </label>
                        <input
                            type="email"
                            required
                            value={formData.email}
                            onChange={(e) => handleInputChange('email', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={() => {
                                setIsModalOpen(false);
                                resetForm();
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            isLoading={createAdminMutation.isPending}
                        >
                            Create Super Admin
                        </Button>
                    </div>
                </form>
            </Modal>
        </div>
    );
};

export default ManageAdmins;
